import React from 'react';

export default function Terms({ onBack }) {
  return (
    <div className="legal-page">
      <div className="legal-wrap">
        <div className="legal-header">
          <button className="legal-back" onClick={onBack}>← Back to CinemaAlert</button>
          <div className="legal-brand">🎬 Cinema Alert</div>
        </div>

        <h1 className="legal-title">Terms of Service</h1>
        <p className="legal-updated">Last updated: March 2026</p>

        <section className="legal-section">
          <h2>1. Service Description</h2>
          <p>
            Cinema Alert is a free notification service that monitors when movie tickets
            become available for purchase at <strong>Megaplex</strong> and{' '}
            <strong>Cinemark</strong> theaters in Utah. When tickets go on sale for a film
            on your watchlist, Cinema Alert sends you an alert via email, SMS, or both —
            based on the notification preference you selected at signup.
          </p>
          <p>
            Cinema Alert is <strong>free to use</strong>. We do not charge for account
            creation, watchlist management, or alert delivery.
          </p>
          <p>
            Cinema Alert is <strong>not affiliated with, endorsed by, or in partnership
            with</strong> Megaplex Theatres or Cinemark. Ticket availability information
            is sourced independently from publicly accessible theater listings.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Opt-In Consent</h2>
          <p>
            By creating an account, you consent to receive notifications from Cinema Alert
            based on your selected preference:
          </p>
          <ul>
            <li>
              <strong>Email alerts</strong> — sent to the email address you provided at signup.
              You must explicitly select email (or both) as your notification method.
            </li>
            <li>
              <strong>SMS alerts</strong> — sent to the phone number you provided at signup.
              You must explicitly select SMS (or both) as your notification method.
              By providing your phone number and selecting SMS, you consent to receive
              automated text messages from Cinema Alert.
            </li>
          </ul>
          <p>
            You can update your notification preference at any time from your Account page.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. Message Frequency</h2>
          <p>
            Cinema Alert sends notifications <strong>only when tickets go on sale</strong>{' '}
            for movies on your watchlist. You will receive one alert per movie per theater
            when availability is first detected. Message frequency varies based on the number
            of movies on your watchlist and upcoming release schedules.
          </p>
        </section>

        <section className="legal-section">
          <h2>4. SMS Terms</h2>
          <ul>
            <li>
              <strong>Msg &amp; data rates may apply.</strong> Standard messaging and data
              rates from your mobile carrier may apply to messages sent and received.
            </li>
            <li>
              <strong>Opt-out:</strong> Reply <strong>STOP</strong> to any Cinema Alert
              text message at any time to unsubscribe. You will receive one final
              confirmation message and no further SMS messages will be sent to that number.
            </li>
            <li>
              <strong>Help:</strong> Reply <strong>HELP</strong> to any message or contact
              us at <a href="mailto:alerts@cinemaalert.io">alerts@cinemaalert.io</a>.
            </li>
            <li>
              Supported carriers include AT&amp;T, T-Mobile, Verizon, and most major US
              carriers. Carrier availability is not guaranteed.
            </li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>5. Email Opt-Out</h2>
          <p>
            Every Cinema Alert email includes an <strong>unsubscribe link</strong> in the
            footer. Clicking it will immediately disable email notifications for your account.
            You can also update or disable email alerts from your Account settings at any time.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. No Theater Affiliation</h2>
          <p>
            CinemaAlert is an independent ticket alert service and is <strong>not affiliated
            with, endorsed by, or partnered with</strong> Megaplex Theatres, Cinemark, or any
            other theater chain. We do not receive compensation from any theater for directing
            users to their websites. We simply monitor publicly available ticket sale
            information and notify users who have opted in to receive alerts.
          </p>
          <p>
            Ticket links in our alerts point to the official theater websites. We have no
            control over ticket pricing, availability, or purchase processes on those sites.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Limitation of Liability</h2>
          <p>
            Cinema Alert is provided "as is" without warranties of any kind. We are not
            responsible for:
          </p>
          <ul>
            <li>Ticket availability or pricing on theater websites</li>
            <li>Missed ticket releases due to monitoring delays or service outages</li>
            <li>Actions taken or not taken based on alerts you receive</li>
            <li>Any losses arising from your use of or inability to use the service</li>
          </ul>
          <p>
            To the maximum extent permitted by applicable law, Cinema Alert's total liability
            to you for any claim arising from use of the service is limited to zero dollars,
            as the service is provided free of charge.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Service Modifications and Discontinuation</h2>
          <p>
            Cinema Alert reserves the right to modify, suspend, or discontinue the service —
            in whole or in part — at any time, with or without notice. We will make reasonable
            efforts to notify registered users of significant changes via email.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. User Responsibilities</h2>
          <p>By using Cinema Alert, you agree to:</p>
          <ul>
            <li>Provide accurate contact information when creating your account</li>
            <li>Keep your password confidential</li>
            <li>Use the service only for its intended personal use</li>
            <li>Not attempt to scrape, reverse-engineer, or otherwise abuse the platform</li>
          </ul>
        </section>

        <section className="legal-section">
          <h2>10. Privacy</h2>
          <p>
            Your use of Cinema Alert is governed by our{' '}
            <a href="/privacy-policy">Privacy Policy</a>, which is incorporated into
            these Terms by reference.
          </p>
        </section>

        <section className="legal-section">
          <h2>Contact</h2>
          <p>Questions about these Terms? Reach us at:</p>
          <p className="legal-contact">
            <a href="mailto:alerts@cinemaalert.io">alerts@cinemaalert.io</a>
          </p>
        </section>
      </div>

      <div className="legal-footer">
        <a href="/">Home</a>
        <span className="legal-footer-sep">·</span>
        <a href="/privacy-policy">Privacy Policy</a>
        <span className="legal-footer-sep">·</span>
        <a href="/contact">Contact</a>
      </div>
    </div>
  );
}
